import { loadStripe } from '@stripe/stripe-js';
import { CardElement } from '@stripe/react-stripe-js';
import { getStripeAmount, getCurrencyForLanguage } from '../utils/currencyConfig';

// Check if Stripe publishable key is available
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  console.warn('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Payment functionality will be disabled.');
}

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

class PaymentService {
  async createPaymentIntent(couponCode = null, language = 'en') {
    if (!stripePublishableKey) {
      throw new Error('Payment service is not configured. Please contact support.');
    }

    try {
      // Use main API worker for payments
      const apiUrl = process.env.REACT_APP_API_URL || 'https://diary.tarotreadingcard.online';
      const body = { 
        language: language, // Pass language for currency detection
        currency: getCurrencyForLanguage(language) // Pass specific currency
      };
      
      if (couponCode) {
        body.couponCode = couponCode;
      }
      
      console.log('Creating payment intent:', { 
        body, 
        apiUrl: apiUrl,
        authToken: localStorage.getItem('authToken') ? 'Present' : 'Missing'
      });
      
      const response = await fetch(`${apiUrl}/api/payment/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(body)
      });

      console.log('Payment intent response:', {
        status: response.status, 
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Payment intent error:', errorData);
        throw new Error(errorData.error || errorData.message || 'Failed to create payment intent');
      }

      const result = await response.json();
      console.log('Payment intent result:', result);
      return result;
    } catch (error) {
      console.error('Payment intent creation failed:', error);
      throw error;
    }
  }

  async processPayment(clientSecret, spreadData, stripe, elements, language = 'en') {
    if (!stripePromise) {
      throw new Error('Payment service is not configured. Please contact support.');
    }

    try {
      if (!stripe || !elements) {
        throw new Error('Payment system not properly initialized. Please refresh the page.');
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        throw new Error('Card information not found. Please refresh the page.');
      }
      
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: card,
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      // Confirm payment with main API worker and get AI explanation
      const apiUrl = process.env.REACT_APP_API_URL || 'https://diary.tarotreadingcard.online';
      const response = await fetch(`${apiUrl}/api/payment/confirm-and-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          spreadData,
          language
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to confirm payment');
      }

      return await response.json();
    } catch (error) {
      console.error('Payment processing failed:', error);
      throw error;
    }
  }

  async generateAIExplanation(spreadData, language = 'en') {
    try {
      // Use main API worker to generate AI explanation for free access
      const apiUrl = process.env.REACT_APP_API_URL || 'https://diary.tarotreadingcard.online';
      const response = await fetch(`${apiUrl}/api/payment/confirm-and-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          paymentIntentId: 'free_access', // Special identifier for free access
          spreadData,
          language,
          freeAccess: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate AI explanation');
      }

      return await response.json();
    } catch (error) {
      console.error('AI explanation generation failed:', error);
      throw error;
    }
  }
}

const paymentServiceInstance = new PaymentService();
export default paymentServiceInstance;
